import React, {useCallback, useEffect} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useShouldShowHotelCardClickersContent} from 'components/Accommodation/AccommodationView/Desktop/useShouldShowHotelCardClickersContent'
import {useViewDealsScrollTarget} from 'components/Accommodation/AccommodationView/Desktop/useViewDealsScrollTarget'
import {scrollToSection} from 'components/ScrollSpy'
import {useOtaMode} from 'hooks/useOtaMode'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {getIsNoDatesSearch} from 'modules/search/selectors'
import {AnimatePresence, motion} from 'motion/react'

import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {DatelessCTA, DatelessCTAVariant} from '../DatelessCTA/DatelessCTA'

const Container = styled.div`
  min-width: 302px;
  display: flex;
  justify-content: flex-end;
`

interface Props {
  isVisible: boolean
  isLoading: boolean
}

export const HeaderCTA: React.FC<Props> = ({isVisible, isLoading}) => {
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {isOTAMode} = useOtaMode()
  const shouldShowHotelCardClickersContent =
    useShouldShowHotelCardClickersContent()

  //in OTA we don't show topOffers, so it's always rooms only
  const shouldScrollToTopOffers =
    !isOTAMode && shouldShowHotelCardClickersContent

  const viewDealsScrollTarget = useViewDealsScrollTarget()
  const {isDesktopSm, isDesktopXs} = useDeviceLayout()
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)

  const handleTrackOffer = useCallback(() => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.System,
        entity: Entity.HotelDetailsStaticOfferCTA,
        action: Action.Displayed
      })
    )
  }, [dispatchTeamEvent])

  useEffect(() => {
    if (!isLoading && isVisible && !shouldScrollToTopOffers) {
      handleTrackOffer()
    }
  }, [isVisible, isLoading, handleTrackOffer, shouldScrollToTopOffers])

  const handleClick = () => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HeaderCTA,
        action: Action.Clicked
      })
    )
    if (shouldScrollToTopOffers) {
      scrollToSection(viewDealsScrollTarget)
      return
    }
    scrollToSection(AccommodationPageNavSections.Rooms)
  }

  const renderCTAText = () => {
    if (isNoDatesSearch) {
      return <DatelessCTA variant={DatelessCTAVariant.DesktopStickyHeader} />
    }

    return (
      <Button size="lg" variant="primary" onClick={handleClick}>
        {shouldScrollToTopOffers ? (
          <FormattedMessageWrapper id="viewDeals" defaultMessage="View deals" />
        ) : (
          <FormattedMessageWrapper id="viewRooms" defaultMessage="View rooms" />
        )}
      </Button>
    )
  }

  const renderCTA = () => {
    if (isLoading)
      return (
        <Container>
          <SkeletonLoader width={144} align="right" />
        </Container>
      )

    return <Container>{renderCTAText()}</Container>
  }
  if (isDesktopSm || isDesktopXs) return null
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
        >
          {renderCTA()}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
